import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { DMARCResult } from "@/components/tool-results";

export function DMARCCheckerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.validateDMARC(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free DMARC Record Checker",
    "description": "Check your DMARC record and policy configuration. Validate DMARC setup for email authentication and get actionable recommendations.",
    "url": "https://getsent.dev/tools/dmarc-checker",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a DMARC record?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DMARC (Domain-based Message Authentication, Reporting & Conformance) is an email authentication protocol that builds on SPF and DKIM. It tells receiving servers how to handle emails that fail authentication and where to send reports about email authentication results."
        }
      },
      {
        "@type": "Question",
        "name": "What DMARC policy should I use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Start with p=none to monitor without affecting delivery. Once you've verified all legitimate email sources are authenticated, move to p=quarantine (spam folder) and eventually p=reject (block) for maximum protection against spoofing."
        }
      },
      {
        "@type": "Question",
        "name": "Is DMARC required?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "As of 2024, Google and Yahoo require DMARC for bulk email senders (5,000+ emails/day). Even for smaller senders, DMARC is strongly recommended to protect your domain from spoofing and improve email deliverability."
        }
      }
    ]
  };

  return (
    <div>
      <SEO
        title="Free DMARC Record Checker - Validate Your DMARC Policy"
        description="Check your DMARC record and policy configuration instantly. Validate p=none, p=quarantine, or p=reject policies. Free DMARC analyzer - no signup required."
        canonical="/tools/dmarc-checker"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-teal-50/50 to-transparent py-16 dark:from-teal-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-teal-500/10 px-4 py-1.5 text-sm font-medium text-teal-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Free Tool
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              DMARC Record{" "}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-600 bg-clip-text text-transparent">
                Checker
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Validate your DMARC policy and check your email authentication configuration.
              Essential for Google and Yahoo compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Tool */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Enter your domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-teal-500 to-cyan-600 px-8 py-3 font-semibold text-white shadow-lg shadow-teal-500/25 transition-all hover:shadow-xl hover:shadow-teal-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check DMARC"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-6">
              <DMARCResult data={result} />
            </div>
          )}
        </div>
      </section>

      {/* What is DMARC */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">What is DMARC?</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              <strong className="text-foreground">DMARC (Domain-based Message Authentication, Reporting & Conformance)</strong>
              is an email authentication protocol that builds on SPF and DKIM. It tells receiving mail servers
              what to do when an email fails authentication checks and provides a way to receive reports
              about email authentication results.
            </p>
            <p>
              A DMARC record looks like this:
            </p>
            <code className="block rounded-lg bg-muted p-4 text-sm text-foreground">
              v=DMARC1; p=reject; rua=mailto:dmarc@example.com; pct=100
            </code>
            <p>
              This record tells receivers to reject all emails that fail DMARC and send aggregate
              reports to dmarc@example.com.
            </p>
          </div>
        </div>
      </section>

      {/* DMARC Policies */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Understanding DMARC Policies</h2>
          <div className="mt-6 grid gap-4">
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-blue-500/10 px-3 py-1 text-sm font-semibold text-blue-600">p=none</span>
                <span className="font-semibold">Monitor Only</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                No action is taken on failing emails. Use this to collect data and identify all
                legitimate sending sources before enforcing. Recommended as a starting point.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-amber-500/10 px-3 py-1 text-sm font-semibold text-amber-600">p=quarantine</span>
                <span className="font-semibold">Send to Spam</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Failing emails are sent to spam/junk folder. A good intermediate step that protects
                recipients while allowing review of quarantined messages.
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-green-500/10 px-3 py-1 text-sm font-semibold text-green-600">p=reject</span>
                <span className="font-semibold">Block Completely</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Failing emails are rejected entirely. Maximum protection against spoofing.
                Only use after verifying all legitimate sources pass authentication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Google/Yahoo Requirements */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Google & Yahoo DMARC Requirements</h2>
          <div className="mt-4 space-y-4 text-muted-foreground">
            <p>
              Starting February 2024, <strong className="text-foreground">Google and Yahoo require DMARC</strong> for
              domains sending more than 5,000 emails per day. Even smaller senders benefit from DMARC
              for better deliverability and protection.
            </p>
            <div className="rounded-xl border bg-amber-500/5 p-5">
              <h3 className="font-semibold text-amber-600">Minimum Requirements</h3>
              <ul className="mt-2 list-disc list-inside text-sm space-y-1">
                <li>Valid SPF record</li>
                <li>Valid DKIM signature</li>
                <li>DMARC record (at minimum p=none)</li>
                <li>Easy one-click unsubscribe for marketing emails</li>
                <li>Spam complaint rate below 0.3%</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold">What is a DMARC record?</h3>
              <p className="mt-2 text-muted-foreground">
                DMARC (Domain-based Message Authentication, Reporting & Conformance) is an email
                authentication protocol that builds on SPF and DKIM. It tells receiving servers
                how to handle emails that fail authentication and where to send reports about
                email authentication results.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What DMARC policy should I use?</h3>
              <p className="mt-2 text-muted-foreground">
                Start with <code>p=none</code> to monitor without affecting delivery. Once you've
                verified all legitimate email sources are authenticated, move to <code>p=quarantine</code>
                (spam folder) and eventually <code>p=reject</code> (block) for maximum protection
                against spoofing.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">Is DMARC required?</h3>
              <p className="mt-2 text-muted-foreground">
                As of 2024, Google and Yahoo require DMARC for bulk email senders (5,000+ emails/day).
                Even for smaller senders, DMARC is strongly recommended to protect your domain from
                spoofing and improve email deliverability.
              </p>
            </div>
            <div>
              <h3 className="font-semibold">What is the "rua" tag in DMARC?</h3>
              <p className="mt-2 text-muted-foreground">
                The <code>rua</code> (reporting URI for aggregate reports) tag specifies where DMARC
                aggregate reports should be sent. These reports show who is sending email using your
                domain and whether they pass or fail authentication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center">Related Tools</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/tools/spf-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-teal-600">SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate your SPF record and check DNS lookup limits.
              </p>
            </Link>
            <Link
              to="/tools/dkim-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-teal-600">DKIM Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate your DKIM records and discover configured selectors.
              </p>
            </Link>
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-teal-600">Full Email Check</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC all at once.
              </p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "complete-guide-dmarc-2026" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <h3 className="font-semibold group-hover:text-teal-600">Complete DMARC Guide</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Everything you need to know about implementing DMARC.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want to analyze your DMARC reports?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get automated DMARC report analysis and see who's sending email as your domain. Free for one domain.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 px-8 py-3 font-semibold text-white shadow-lg shadow-teal-500/25 transition-all hover:shadow-xl hover:shadow-teal-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}
